import type { ForwardRefRenderFunction } from 'react';

import Calendar from '@crehana/icons/outline/Calendar';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';
import { clsx } from 'clsx';
import moment, { type Moment } from 'moment';
import DateInput from 'react-datetime';

import 'moment/locale/es';
import 'moment/locale/pt';

import { DatePickerWrapper } from './DatePicker.styles';
import type { DatePickerProps } from './types';

const { baseLighter } = colors;

const DatePicker: ForwardRefRenderFunction<
  HTMLInputElement,
  DatePickerProps
> = ({
  className,
  css,
  errorText,
  hint,
  isDark = true,
  label,
  langCode,
  limits,
  name,
  onSelect,
  rtl,
  value,
  //TODO: onBlur and ref are destructured out to prevent passing them down this component, remove after DateInput (or its replacement) supports them
  // TODO: talk with the code owner to fix this code so we can enable this rule again

  onBlur,
  // TODO: talk with the code owner to fix this code so we can enable this rule again

  ref,
  ...restProps
}) => {
  const textColorClass = isDark ? 'text-white' : 'text-body';
  const inputErrorClass = errorText
    ? 'border-red-main text-base-main caret-red-main'
    : null;
  const inputColorClass = isDark
    ? `bg-gray-main-dark-mode ${
        !inputErrorClass ? 'border-transparent' : inputErrorClass
      }`
    : `bg-white ${!inputErrorClass ? 'border-gray-dark' : inputErrorClass}`;

  const validateLimits = (day: Moment) => {
    if (!limits) {
      // Preserved previous behaviour, but needs to be changed to return true
      return day.isAfter(moment().subtract(1, 'day'));
    }
    if (limits.minDate && limits.maxDate) {
      return day.isAfter(limits.minDate) && day.isBefore(limits.maxDate);
    }
    if (limits.minDate) {
      return day.isAfter(limits.minDate);
    }
    if (limits.maxDate) {
      return day.isBefore(limits.maxDate);
    }
    return true;
  };

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: label association handled differently
    <label className={`inline-flex flex-col ${className}`}>
      {label && (
        <span
          className={clsx(
            'font-subtitle3 mb-8',
            isDark ? 'text-white' : 'text-base-main',
          )}
        >
          {label}
        </span>
      )}
      <DatePickerWrapper isDark={isDark} rtl={rtl}>
        <DateInput
          locale={langCode || 'es'}
          isValidDate={validateLimits}
          timeFormat={false}
          value={value || ''}
          className={`rounded-5 border border-solid ${inputColorClass}`}
          onChange={val =>
            onSelect && onSelect(moment(val).format('DD/MM/YYYY'))
          }
          renderInput={(props, openCalendar) => (
            <div className={clsx('flex w-full items-end')}>
              <input
                {...props}
                {...restProps}
                value={value || ''}
                onClick={() => openCalendar()}
                name={name}
                autoComplete="off"
                readOnly
                className={`sc-7y9917-0 dgURZd crehana-ui-input w-full z-1 border-0 resize-none focus:outline-none min-h-40 bg-transparent font-body3 py-16 pl-16 pr-8" ${textColorClass}`}
                placeholder={hint}
                css={css}
              />
              <div
                className="flex items-center pr-16 h-56"
                style={{
                  transition:
                    'padding 150ms ease-in-out 0s, height 150ms ease-in-out 0s',
                }}
              >
                <Calendar color={baseLighter} size={24} />
              </div>
            </div>
          )}
        />
      </DatePickerWrapper>

      {errorText && (
        <span className="font-caption mt-8 text-red-main" role="alert">
          {errorText}
        </span>
      )}
    </label>
  );
};

export default DatePicker;
